import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, MapPin, Calendar, CheckCircle, Star, Feather, BookOpen, Heart, Eye, 
  User, Globe, InstagramLogo, TwitterLogo, LinkedinLogo, MagnifyingGlass, Trophy,
  Palette, Bookmark, ChatCircle, GraduationCap, Pen, Sparkle, Lightning, Fire,
  TrendUp, Books, Target, Clock, Flag, Pencil, UsersThree, Article, GridFour, List
} from '@phosphor-icons/react';
import { proxyImage } from '../lib/imageProxy';
import GlobalHeader from '../components/GlobalHeader';
import Footer from '../components/Footer';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const WriterProfileEnhanced = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [writer, setWriter] = useState(null);
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [worksLoading, setWorksLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('works');
  const [worksFilter, setWorksFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('latest');
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  const fetchWriter = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/writers/${username}`);
      setWriter(res.data);
    } catch (error) {
      console.error(error);
      toast.error('Writer not found');
    } finally {
      setLoading(false);
    }
  }, [username]);

  const checkFollowStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/writers/${username}/follow-status`);
      setIsFollowing(res.data.following);
    } catch (error) {
      console.error(error);
    }
  }, [username]);

  const fetchWorks = useCallback(async () => {
    try {
      setWorksLoading(true);
      const params = new URLSearchParams({ sort, limit: 50 });
      if (worksFilter !== 'all') params.append('content_type', worksFilter);
      if (search) params.append('search', search);

      const res = await axios.get(`${API}/api/writers/${username}/works?${params.toString()}`);
      setWorks(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setWorksLoading(false);
    }
  }, [username, sort, worksFilter, search]);

  useEffect(() => {
    fetchWriter();
  }, [fetchWriter]);

  useEffect(() => {
    if (writer && user && user.user_id !== writer.user_id) {
      checkFollowStatus();
    }
  }, [writer, user, checkFollowStatus]);

  useEffect(() => {
    if (writer && activeTab === 'works') {
      fetchWorks();
    }
  }, [writer, activeTab, fetchWorks]);

  const handleFollow = async () => {
    if (!user) {
      toast.error('Please login to follow writers');
      return;
    }

    try {
      setFollowLoading(true);
      if (isFollowing) {
        await axios.delete(`${API}/api/writers/${username}/follow`);
        setIsFollowing(false);
        setWriter({ ...writer, followers_count: writer.followers_count - 1 });
        toast.success('Unfollowed successfully');
      } else {
        await axios.post(`${API}/api/writers/${username}/follow`);
        setIsFollowing(true);
        setWriter({ ...writer, followers_count: writer.followers_count + 1 });
        toast.success('Following successfully');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update follow status');
    } finally {
      setFollowLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <GlobalHeader />
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin h-8 w-8 border-b-2 border-primary rounded-full" />
        </div>
      </div>
    );
  }

  if (!writer) {
    return (
      <div className="min-h-screen bg-background">
        <GlobalHeader />
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h1 className="text-2xl font-semibold text-foreground mb-4">Writer Not Found</h1>
          <p className="text-muted-foreground mb-6">The writer you're looking for doesn't exist.</p>
          <Link to="/authors/community" className="text-primary hover:underline">
            Browse Community Writers
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />

      {/* Hero Banner - Enhanced */}
      {writer && (
        <div className="relative h-72 md:h-96 bg-gradient-to-br from-primary/20 via-primary/10 to-background overflow-hidden">
          {writer.cover_banner ? (
            <>
              <img
                src={proxyImage(writer.cover_banner)}
                alt="Cover"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
            </>
          ) : (
            <>
              {/* Default Literary Banner */}
              <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-amber-50 to-background dark:from-orange-950/20 dark:via-amber-950/10 dark:to-background">
                <div className="absolute inset-0 opacity-10">
                  <svg className="w-full h-full" viewBox="0 0 800 400" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <pattern id="literary-pattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                        <text x="10" y="20" fontSize="24" fill="currentColor" opacity="0.3">✒</text>
                      </pattern>
                    </defs>
                    <rect width="800" height="400" fill="url(#literary-pattern)" />
                  </svg>
                </div>
                <div className="absolute top-20 left-20 opacity-20">
                  <Feather size={200} className="text-primary" weight="thin" />
                </div>
                <div className="absolute bottom-20 right-20 opacity-15">
                  <BookOpen size={180} className="text-primary" weight="thin" />
                </div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
            </>
          )}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 -mt-24 relative z-10 pb-12">
        {/* Back Button - Updated position */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-background/90 hover:text-background mb-4 transition-colors drop-shadow-md"
        >
          <ArrowLeft size={20} weight="bold" />
          <span className="font-medium">Back</span>
        </button>

        {writer && (
          <>
            {/* Profile Card - Enhanced with overlapping avatar */}
            <div className="bg-card border border-border rounded-2xl p-8 mb-8 shadow-xl">
              <div className="flex flex-col md:flex-row gap-8 items-start">
                {/* Avatar - Larger and overlapping banner */}
                <div className="flex-shrink-0 -mt-20">
                  <div className="relative w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden border-4 border-background shadow-2xl bg-card">
                    {writer.picture ? (
                      <img src={proxyImage(writer.picture)} alt={writer.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/30 to-primary/10">
                        <Feather size={80} className="text-primary/50" weight="duotone" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Info - Enhanced */}
                <div className="flex-1">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h1 className="font-serif text-3xl md:text-5xl font-bold text-foreground">
                          {writer.pen_name || writer.name}
                        </h1>
                        {writer.is_verified && (
                          <CheckCircle size={32} weight="fill" className="text-blue-500 flex-shrink-0" title="Verified Writer" />
                        )}
                        {writer.is_featured && (
                          <Star size={32} weight="fill" className="text-amber-500 flex-shrink-0" title="Featured Writer" />
                        )}
                      </div>
                      
                      {/* Pen name + Real name */}
                      {writer.pen_name && writer.pen_name !== writer.name && (
                        <p className="text-muted-foreground mb-2">{writer.name}</p>
                      )}
                      
                      {/* Custom Tagline or Role */}
                      <p className="text-lg md:text-xl text-primary font-medium mb-2">
                        {writer.short_intro || writer.personal_motto || `${writer.role.charAt(0).toUpperCase() + writer.role.slice(1)}`}
                      </p>
                      
                      {/* Profile Quote - Featured prominently */}
                      {writer.profile_quote && (
                        <blockquote className="italic text-foreground/80 mt-3 text-base md:text-lg border-l-4 border-primary pl-4">
                          "{writer.profile_quote}"
                        </blockquote>
                      )}
                    </div>

                    {/* Actions - Enhanced */}
                    <div className="flex gap-3">
                      {user && user.user_id === writer.user_id ? (
                        // Owner view
                        <Link
                          to="/settings/profile"
                          className="px-6 py-3 rounded-lg font-semibold bg-primary text-primary-foreground hover:opacity-90 shadow-md transition-all flex items-center gap-2"
                        >
                          <Pencil size={20} weight="bold" />
                          Edit Profile
                        </Link>
                      ) : user ? (
                        // Visitor view (logged in)
                        <button
                          onClick={handleFollow}
                          disabled={followLoading}
                          className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                            isFollowing
                              ? 'bg-muted text-foreground border-2 border-border hover:bg-muted/80'
                              : 'bg-primary text-primary-foreground hover:opacity-90 shadow-md'
                          } ${followLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {followLoading ? 'Loading...' : isFollowing ? (
                            <><CheckCircle size={20} weight="fill" /> Following</>
                          ) : (
                            <><User size={20} weight="bold" /> Follow</>
                          )}
                        </button>
                      ) : null}
                    </div>
                  </div>

                  {/* Location & Joined */}
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
                    {writer.state && (
                      <div className="flex items-center gap-2">
                        <MapPin size={18} weight="fill" />
                        <span>{writer.state}, {writer.country}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Calendar size={18} weight="fill" />
                      <span>Joined {new Date(writer.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                    </div>
                    {writer.languages_written && writer.languages_written.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Pen size={18} weight="fill" />
                        <span>Writes in {writer.languages_written.join(', ')}</span>
                      </div>
                    )}
                  </div>

                  {/* Bio */}
                  {writer.bio && (
                    <p className="text-foreground leading-relaxed mb-4 max-w-3xl">
                      {writer.bio}
                    </p>
                  )}

                  {/* Stats Grid - Enhanced with more metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
                    <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg p-4 text-center border border-primary/20">
                      <div className="text-2xl md:text-3xl font-bold text-foreground">{writer.works_count}</div>
                      <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Works</div>
                    </div>
                    <div className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 rounded-lg p-4 text-center border border-blue-500/20">
                      <div className="text-2xl md:text-3xl font-bold text-foreground">{writer.followers_count}</div>
                      <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Followers</div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 rounded-lg p-4 text-center border border-purple-500/20">
                      <div className="text-2xl md:text-3xl font-bold text-foreground">{writer.following_count || 0}</div>
                      <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Following</div>
                    </div>
                    <div className="bg-gradient-to-br from-green-500/10 to-green-500/5 rounded-lg p-4 text-center border border-green-500/20">
                      <div className="text-2xl md:text-3xl font-bold text-foreground">{writer.total_views?.toLocaleString() || 0}</div>
                      <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Views</div>
                    </div>
                    <div className="bg-gradient-to-br from-red-500/10 to-red-500/5 rounded-lg p-4 text-center border border-red-500/20">
                      <div className="text-2xl md:text-3xl font-bold text-foreground">{writer.total_likes?.toLocaleString() || 0}</div>
                      <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Likes</div>
                    </div>
                    <div className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 rounded-lg p-4 text-center border border-amber-500/20">
                      <div className="text-2xl md:text-3xl font-bold text-foreground">{writer.total_bookmarks?.toLocaleString() || 0}</div>
                      <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Bookmarks</div>
                    </div>
                  </div>

                  {/* Genres & Styles */}
                  {(writer.genres?.length > 0 || writer.writing_styles?.length > 0) && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {writer.genres?.map((genre) => (
                        <span key={genre} className="px-3 py-1 text-sm bg-primary/10 text-primary rounded-full border border-primary/20">
                          {genre}
                        </span>
                      ))}
                      {writer.writing_styles?.map((style) => (
                        <span key={style} className="px-3 py-1 text-sm bg-secondary/10 text-secondary rounded-full border border-secondary/20">
                          {style}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Social Links */}
                  {(writer.website || writer.social_links) && (
                    <div className="flex gap-3">
                      {writer.website && (
                        <a href={writer.website} target="_blank" rel="noopener noreferrer"
                          className="p-2 border border-border rounded-lg hover:bg-muted transition-colors" title="Website">
                          <Globe size={20} />
                        </a>
                      )}
                      {writer.social_links?.twitter && (
                        <a href={writer.social_links.twitter} target="_blank" rel="noopener noreferrer"
                          className="p-2 border border-border rounded-lg hover:bg-muted transition-colors" title="Twitter">
                          <TwitterLogo size={20} />
                        </a>
                      )}
                      {writer.social_links?.instagram && (
                        <a href={writer.social_links.instagram} target="_blank" rel="noopener noreferrer"
                          className="p-2 border border-border rounded-lg hover:bg-muted transition-colors" title="Instagram">
                          <InstagramLogo size={20} />
                        </a>
                      )}
                      {writer.social_links?.linkedin && (
                        <a href={writer.social_links.linkedin} target="_blank" rel="noopener noreferrer"
                          className="p-2 border border-border rounded-lg hover:bg-muted transition-colors" title="LinkedIn">
                          <LinkedinLogo size={20} />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Tabs Navigation */}
            <div className="bg-card border border-border rounded-xl p-2 mb-8 flex gap-2 overflow-x-auto">
              {[
                { id: 'works', label: 'Works', icon: BookOpen },
                { id: 'about', label: 'About', icon: User },
                { id: 'milestones', label: 'Timeline', icon: Clock },
                { id: 'achievements', label: 'Achievements', icon: Trophy }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <tab.icon size={20} weight={activeTab === tab.id ? 'fill' : 'regular'} />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="space-y-8">
              {/* Works Tab */}
              {activeTab === 'works' && (
                <div className="bg-card border border-border rounded-2xl p-6">
                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-6 flex items-center gap-2">
                    <BookOpen size={28} weight="duotone" />
                    Published Works ({writer.works_count})
                  </h2>

                  {/* Filters */}
                  <div className="mb-6">
                    <div className="flex flex-wrap gap-2 mb-4">
                      <button
                        onClick={() => setWorksFilter('all')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          worksFilter === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                      >
                        All ({writer.works_count})
                      </button>
                      {writer.works_by_type && Object.entries(writer.works_by_type).map(([type, count]) => (
                        <button
                          key={type}
                          onClick={() => setWorksFilter(type)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
                            worksFilter === type ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                          }`}
                        >
                          {type} ({count})
                        </button>
                      ))}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="flex-1 relative">
                        <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                          type="text"
                          placeholder="Search works..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-foreground"
                        />
                      </div>
                      <select
                        value={sort}
                        onChange={(e) => setSort(e.target.value)}
                        className="px-4 py-2 border border-border rounded-lg bg-background text-foreground"
                      >
                        <option value="latest">Latest</option>
                        <option value="oldest">Oldest</option>
                        <option value="most_read">Most Read</option>
                        <option value="most_liked">Most Liked</option>
                        <option value="alphabetical">A-Z</option>
                      </select>
                    </div>
                  </div>

                  {/* Works List with Enhanced Empty States */}
                  {worksLoading ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin h-8 w-8 border-b-2 border-primary rounded-full" />
                    </div>
                  ) : (!works || works.length === 0) ? (
                    <div className="text-center py-16 px-6">
                      <div className="mb-6">
                        <Feather size={80} className="text-muted-foreground/30 mx-auto mb-4" weight="duotone" />
                        {user && user.user_id === writer.user_id ? (
                          // Owner view - no works
                          <>
                            <h3 className="text-xl font-semibold text-foreground mb-2">Start Your Literary Journey</h3>
                            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                              You haven't published any works yet. Share your first poem, story, or article with the Kavyalekh community.
                            </p>
                            <Link
                              to="/write"
                              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 transition-opacity"
                            >
                              <Pen size={20} weight="bold" />
                              Write Now
                            </Link>
                          </>
                        ) : (
                          // Visitor view - no works
                          <>
                            <h3 className="text-xl font-semibold text-foreground mb-2">No Published Works Yet</h3>
                            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                              {writer.name} hasn't published anything yet. Follow them to be notified when they publish their first work.
                            </p>
                            {!isFollowing && (
                              <button
                                onClick={handleFollow}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 transition-opacity"
                              >
                                <User size={20} weight="bold" />
                                Follow {writer.name}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {works.map((work) => (
                        <Link
                          key={work.content_id || work.work_id}
                          to={`/work/${work.slug || work.content_id || work.work_id}`}
                          className="block p-4 border border-border rounded-lg hover:border-primary/50 hover:shadow-md transition-all"
                        >
                          <div className="flex items-start gap-4">
                            {work.featured_image && (
                              <div className="w-24 h-24 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                                <img src={proxyImage(work.featured_image)} alt={work.title} className="w-full h-full object-cover" />
                              </div>
                            )}
                            <div className="flex-1">
                              <h3 className="font-serif text-xl font-semibold text-foreground mb-1">{work.title}</h3>
                              <p className="text-sm text-muted-foreground mb-2 capitalize">
                                {work.content_type || work.work_type} • {new Date(work.created_at).toLocaleDateString()}
                              </p>
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <div className="flex items-center gap-1">
                                  <Eye size={16} />
                                  <span>{work.views || 0}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Heart size={16} />
                                  <span>{work.likes || 0}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* About Tab */}
              {activeTab === 'about' && (
                <div className="space-y-6">
                  {/* Literary Information */}
                  {(writer.themes?.length > 0 || writer.influences?.length > 0 || writer.favorite_authors?.length > 0 || writer.education) && (
                    <div className="bg-card border border-border rounded-2xl p-6">
                      <h3 className="font-serif text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                        <GraduationCap size={24} weight="duotone" />
                        Literary Background
                      </h3>
                      <div className="space-y-4">
                        {writer.themes?.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Themes</h4>
                            <div className="flex flex-wrap gap-2">
                              {writer.themes.map((theme) => (
                                <span key={theme} className="px-3 py-1 bg-muted text-foreground rounded-full text-sm">{theme}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {writer.influences?.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Influences</h4>
                            <p className="text-foreground">{writer.influences.join(', ')}</p>
                          </div>
                        )}
                        {writer.favorite_authors?.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Favorite Authors</h4>
                            <p className="text-foreground">{writer.favorite_authors.join(', ')}</p>
                          </div>
                        )}
                        {writer.education && (
                          <div>
                            <h4 className="text-sm font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Education</h4>
                            <p className="text-foreground">{writer.education}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Timeline Tab */}
              {activeTab === 'milestones' && writer.milestones && writer.milestones.length > 0 && (
                <div className="bg-card border border-border rounded-2xl p-6">
                  <h3 className="font-serif text-xl font-semibold text-foreground mb-6 flex items-center gap-2">
                    <Clock size={24} weight="duotone" />
                    Timeline
                  </h3>
                  <div className="space-y-4">
                    {writer.milestones.map((milestone, index) => (
                      <div key={index} className="flex gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                          {milestone.icon === 'user-plus' && <User size={24} className="text-primary" weight="duotone" />}
                          {milestone.icon === 'book' && <BookOpen size={24} className="text-primary" weight="duotone" />}
                          {milestone.icon === 'users' && <User size={24} className="text-primary" weight="duotone" />}
                          {milestone.icon === 'star' && <Star size={24} className="text-primary" weight="duotone" />}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-foreground">{milestone.title}</h4>
                          {milestone.description && <p className="text-sm text-muted-foreground">{milestone.description}</p>}
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(milestone.date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Achievements Tab - Enhanced with Badges */}
              {activeTab === 'achievements' && (
                <div className="space-y-6">
                  {/* Achievement Badges */}
                  <div className="bg-card border border-border rounded-2xl p-6">
                    <h3 className="font-serif text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Sparkle size={24} weight="duotone" className="text-primary" />
                      Achievement Badges
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {/* Dynamic badges based on milestones */}
                      {writer.works_count > 0 && (
                        <div className="text-center p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                          <div className="text-4xl mb-2">📖</div>
                          <div className="font-semibold text-sm">First Publication</div>
                        </div>
                      )}
                      {writer.works_count >= 10 && (
                        <div className="text-center p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                          <div className="text-4xl mb-2">📝</div>
                          <div className="font-semibold text-sm">10 Published Works</div>
                        </div>
                      )}
                      {writer.followers_count >= 100 && (
                        <div className="text-center p-4 bg-purple-500/10 rounded-lg border border-purple-500/20">
                          <div className="text-4xl mb-2">👥</div>
                          <div className="font-semibold text-sm">100 Followers</div>
                        </div>
                      )}
                      {writer.total_likes >= 100 && (
                        <div className="text-center p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                          <div className="text-4xl mb-2">❤️</div>
                          <div className="font-semibold text-sm">100 Likes</div>
                        </div>
                      )}
                      {writer.is_featured && (
                        <div className="text-center p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
                          <div className="text-4xl mb-2">⭐</div>
                          <div className="font-semibold text-sm">Featured Writer</div>
                        </div>
                      )}
                      {writer.is_verified && (
                        <div className="text-center p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                          <div className="text-4xl mb-2">✓</div>
                          <div className="font-semibold text-sm">Verified Writer</div>
                        </div>
                      )}
                      
                      {/* Show "New Writer" badge if no major achievements */}
                      {writer.works_count === 0 && writer.followers_count < 10 && (
                        <div className="text-center p-4 bg-primary/10 rounded-lg border border-primary/20">
                          <div className="text-4xl mb-2">🌱</div>
                          <div className="font-semibold text-sm">New Writer</div>
                        </div>
                      )}
                    </div>
                    
                    {/* Upcoming Milestones */}
                    {writer.works_count < 10 || writer.followers_count < 100 || writer.total_likes < 100 ? (
                      <div className="mt-6 p-4 bg-muted/30 rounded-lg">
                        <h4 className="font-semibold text-sm text-muted-foreground mb-3 uppercase tracking-wide">Upcoming Milestones</h4>
                        <div className="space-y-2 text-sm text-muted-foreground">
                          {writer.works_count < 10 && (
                            <div className="flex items-center gap-2">
                              <Target size={16} />
                              <span>Publish {10 - writer.works_count} more work{10 - writer.works_count !== 1 ? 's' : ''} to earn "10 Published Works" badge</span>
                            </div>
                          )}
                          {writer.followers_count < 100 && (
                            <div className="flex items-center gap-2">
                              <UsersThree size={16} />
                              <span>Gain {100 - writer.followers_count} more followers to earn "100 Followers" badge</span>
                            </div>
                          )}
                          {writer.total_likes < 100 && (
                            <div className="flex items-center gap-2">
                              <Heart size={16} />
                              <span>Receive {100 - writer.total_likes} more likes to earn "100 Likes" badge</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  {/* Awards - Enhanced */}
                  {writer.awards && writer.awards.length > 0 && (
                    <div className="bg-card border border-border rounded-2xl p-6">
                      <h3 className="font-serif text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                        <Trophy size={24} weight="duotone" className="text-amber-500" />
                        Awards
                      </h3>
                      <div className="space-y-3">
                        {writer.awards.map((award, index) => (
                          <div key={index} className="flex items-start gap-3 p-3 bg-amber-500/5 rounded-lg border border-amber-500/20">
                            <Trophy size={20} className="text-amber-500 flex-shrink-0 mt-1" weight="fill" />
                            <div>
                              <p className="font-semibold text-foreground">{award.title || award}</p>
                              {award.year && <p className="text-sm text-muted-foreground">{award.year}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Achievements */}
                  {writer.achievements?.length > 0 && (
                    <div className="bg-card border border-border rounded-2xl p-6">
                      <h3 className="font-serif text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                        <Target size={24} weight="duotone" className="text-primary" />
                        Achievements
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {writer.achievements.map((achievement, index) => (
                          <div key={index} className="flex items-center gap-3 p-3 bg-primary/5 rounded-lg border border-primary/20">
                            <Sparkle size={20} className="text-primary flex-shrink-0" weight="fill" />
                            <p className="text-foreground">{achievement}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default WriterProfileEnhanced;
